import React, { useState } from 'react';

type TooltipPosition = 'top' | 'bottom' | 'left' | 'right';
type TooltipVariant = 'brand' | 'dark';

interface TooltipComponentProps {
  title: string;
  position?: TooltipPosition;
  // 'brand' = the default blue bubble; 'dark' = the app's dark surface (palette-black-1),
  // a calmer look for dense UI.
  variant?: TooltipVariant;
  children: React.ReactNode;
  zIndex?: number;
  // Put on the bubble so a control can point `aria-describedby` at it instead of repeating the
  // text in a second, screen-reader-only element.
  id?: string;
  // Wrap onto more lines within a fixed width instead of running on as one line. For sentences
  // that sit inside a narrow, overflow-hidden pane, where one long line would be cut off.
  multiline?: boolean;
}

// Bubble + arrow colors per variant. Kept as literal class strings so Tailwind's JIT emits
// them (dynamically interpolated color tokens would be purged).
const BUBBLE_COLOR: Record<TooltipVariant, string> = {
  brand: 'bg-brand-primary',
  dark: 'border border-palette-border bg-palette-black-1',
};

const ARROW_COLOR: Record<TooltipVariant, Record<TooltipPosition, string>> = {
  brand: {
    top: 'border-t-brand-primary',
    bottom: 'border-b-brand-primary',
    left: 'border-l-brand-primary',
    right: 'border-r-brand-primary',
  },
  dark: {
    top: 'border-t-palette-black-1',
    bottom: 'border-b-palette-black-1',
    left: 'border-l-palette-black-1',
    right: 'border-r-palette-black-1',
  },
};

const TooltipComponent: React.FC<TooltipComponentProps> = ({
  children,
  title,
  position = 'top',
  variant = 'brand',
  zIndex = 9999,
  id,
  multiline = false,
}) => {
  const [isVisible, setIsVisible] = useState(false);

  const getTooltipClasses = () => {
    // Beside the trigger, a one-line bubble centers on it. A wrapped one is taller than its
    // trigger, so centered it would poke above a header row and get cut off by a clipping pane;
    // it lines up with the trigger's top and grows downward instead.
    const sideAlign = multiline ? 'top-0' : 'top-1/2 -translate-y-1/2';
    const baseClasses = `absolute pointer-events-none transition-all duration-300 ease-in-out ${
      isVisible ? 'opacity-95 visible' : 'opacity-0 invisible'
    } ${multiline ? 'w-max max-w-[15rem]' : ''}`;

    switch (position) {
      case 'top':
        return `${baseClasses} bottom-full left-1/2 -translate-x-1/2 ${
          isVisible ? '-translate-y-0' : '-translate-y-2'
        } mb-1`;
      case 'bottom':
        return `${baseClasses} top-full left-1/2 -translate-x-1/2 ${
          isVisible ? 'translate-y-0' : 'translate-y-2'
        } mt-1`;
      case 'left':
        return `${baseClasses} right-full ${sideAlign} ${
          isVisible ? '-translate-x-0' : '-translate-x-2'
        } mr-1`;
      case 'right':
        return `${baseClasses} left-full ${sideAlign} ${
          isVisible ? 'translate-x-0' : 'translate-x-2'
        } ml-1`;
      default:
        return `${baseClasses} bottom-full left-1/2 -translate-x-1/2 ${
          isVisible ? '-translate-y-0' : '-translate-y-2'
        } mb-1`;
    }
  };

  const getArrowClasses = () => {
    const baseArrowClasses = 'absolute w-0 h-0';
    // A top-aligned (wrapped) bubble points from beside its first line, level with the middle of
    // a standard 34px control.
    const sideArrowAlign = multiline ? 'top-3' : 'top-1/2 -translate-y-1/2';
    const arrowColor = ARROW_COLOR[variant][position] ?? ARROW_COLOR[variant].top;

    switch (position) {
      case 'top':
        return `${baseArrowClasses} top-full left-1/2 -translate-x-1/2 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent ${arrowColor}`;
      case 'bottom':
        return `${baseArrowClasses} bottom-full left-1/2 -translate-x-1/2 border-l-4 border-r-4 border-b-4 border-l-transparent border-r-transparent ${arrowColor}`;
      case 'left':
        return `${baseArrowClasses} left-full ${sideArrowAlign} border-t-4 border-b-4 border-l-4 border-t-transparent border-b-transparent ${arrowColor}`;
      case 'right':
        return `${baseArrowClasses} right-full ${sideArrowAlign} border-t-4 border-b-4 border-r-4 border-t-transparent border-b-transparent ${arrowColor}`;
      default:
        return `${baseArrowClasses} top-full left-1/2 -translate-x-1/2 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent ${arrowColor}`;
    }
  };

  return (
    <div className="relative inline-flex">
      <div
        className="flex w-full"
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
        onFocus={() => setIsVisible(true)}
        onBlur={() => setIsVisible(false)}
      >
        {children}
      </div>
      {/* No title, no bubble: a caller can keep the wrapper mounted while it has nothing to say. */}
      {title && (
        <div className={getTooltipClasses()} style={{ zIndex }}>
          <div
            id={id}
            role="tooltip"
            className={`relative flex h-full min-h-[22px] items-center rounded ${
              BUBBLE_COLOR[variant]
            } px-2 py-1 text-xs font-normal ${
              multiline ? 'whitespace-normal leading-tight' : 'whitespace-nowrap leading-none'
            } text-white shadow-lg`}
          >
            {title}
            <div className={getArrowClasses()}></div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TooltipComponent;
